import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { api } from '../services/api';

const MAX_CONCURRENT = 3;
const POLL_INTERVAL_MS = 2000;

const UploadContext = createContext(null);

export function UploadProvider({ children }) {
  const [jobs, setJobs] = useState({});
  const [showPanel, setShowPanel] = useState(false);

  // These refs survive navigation — they live in the provider which is mounted once at App level.
  const activeCount = useRef(0);
  const queue = useRef([]); // { entryId, file, formDataBuilder, uploadFn, batchId }
  const pollTimers = useRef({});
  const batches = useRef({}); // { [batchId]: { onAllComplete, onFileComplete, pendingJobIds: Set } }
  const allDoneCheckRef = useRef({});

  const checkBatchComplete = useCallback((batchId) => {
    if (allDoneCheckRef.current[batchId]) clearTimeout(allDoneCheckRef.current[batchId]);
    allDoneCheckRef.current[batchId] = setTimeout(() => {
      setJobs(currentJobs => {
        const batch = batches.current[batchId];
        if (!batch) return currentJobs;
        const pending = batch.pendingJobIds;
        const allDone = [...pending].every(id => {
          const j = currentJobs[id];
          return !j || j.status === 'completed' || j.status === 'error';
        });
        if (allDone) {
          const batchJobs = [...pending].map(id => currentJobs[id]).filter(Boolean);
          const successCount = batchJobs.filter(j => j.status === 'completed').length;
          const failedCount = batchJobs.filter(j => j.status === 'error').length;
          if (batch.onAllComplete) {
            setTimeout(() => batch.onAllComplete(successCount, failedCount), 0);
          }
          delete batches.current[batchId];
        }
        return currentJobs;
      });
    }, 300);
  }, []);

  const pollJob = useCallback((entryId, jobId, batchId, onFileComplete) => {
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await api.getJobStatus(jobId);
        const { status, progress, stage, error_message, result_json } = res.data;
        const displayProgress = 50 + (progress || 0) * 50;

        setJobs(prev => ({
          ...prev,
          [entryId]: {
            ...prev[entryId],
            progress: Math.round(displayProgress),
            stage,
            status: status === 'completed' ? 'completed' : status === 'failed' ? 'error' : prev[entryId]?.status,
            error: (typeof error_message === 'string' ? error_message : null) || prev[entryId]?.error,
          },
        }));

        if (status === 'completed') {
          clearInterval(timer);
          delete pollTimers.current[entryId];
          if (onFileComplete) onFileComplete(result_json);
          checkBatchComplete(batchId);
        } else if (status === 'failed') {
          clearInterval(timer);
          delete pollTimers.current[entryId];
          checkBatchComplete(batchId);
        }
      } catch (err) {
        console.warn(`Poll error for job ${jobId}:`, err);
      }
    }, POLL_INTERVAL_MS);

    pollTimers.current[entryId] = timer;
  }, [checkBatchComplete]);

  const processQueue = useCallback(() => {
    while (queue.current.length > 0 && activeCount.current < MAX_CONCURRENT) {
      const { entryId, file, formDataBuilder, uploadFn, batchId, onFileComplete } = queue.current.shift();
      activeCount.current++;

      (async () => {
        try {
          const formData = formDataBuilder(file);

          const response = await uploadFn(formData, (progressEvent) => {
            const pct = progressEvent.total
              ? Math.round((progressEvent.loaded / progressEvent.total) * 50)
              : 0;
            setJobs(prev => ({
              ...prev,
              [entryId]: { ...prev[entryId], progress: pct, status: 'uploading' },
            }));
          });

          const jobId = response.data?.job_id;

          if (jobId) {
            setJobs(prev => ({
              ...prev,
              [entryId]: { ...prev[entryId], jobId, status: 'processing', progress: 50, stage: 'queued' },
            }));
            pollJob(entryId, jobId, batchId, onFileComplete);
          } else {
            setJobs(prev => ({
              ...prev,
              [entryId]: { ...prev[entryId], progress: 100, status: 'completed' },
            }));
            checkBatchComplete(batchId);
          }
        } catch (err) {
          const rawDetail = err.response?.data?.detail;
          const msg = typeof rawDetail === 'string' ? rawDetail : (err.message || 'Upload failed');
          setJobs(prev => ({
            ...prev,
            [entryId]: { ...prev[entryId], status: 'error', error: msg },
          }));
          checkBatchComplete(batchId);
        } finally {
          activeCount.current--;
          processQueue();
        }
      })();
    }
  }, [pollJob, checkBatchComplete]);

  const upload = useCallback((files, formDataBuilder, uploadFn, { onAllComplete, onFileComplete } = {}) => {
    const batchId = Math.random().toString(36).slice(2);
    const newJobs = {};
    const entries = [];
    const pendingJobIds = new Set();

    for (const file of files) {
      const entryId = Math.random().toString(36).slice(2);
      newJobs[entryId] = { fileName: file.name, progress: 0, status: 'queued', stage: null, error: null, jobId: null };
      entries.push({ entryId, file, formDataBuilder, uploadFn, batchId, onFileComplete });
      pendingJobIds.add(entryId);
    }

    batches.current[batchId] = { onAllComplete, onFileComplete, pendingJobIds };
    setJobs(prev => ({ ...prev, ...newJobs }));
    setShowPanel(true);
    queue.current.push(...entries);
    processQueue();
  }, [processQueue]);

  const clearCompleted = useCallback(() => {
    setJobs(prev => {
      const next = {};
      for (const [id, job] of Object.entries(prev)) {
        if (job.status !== 'completed' && job.status !== 'error') next[id] = job;
      }
      return next;
    });
  }, []);

  const jobValues = Object.values(jobs);
  const hasActive = jobValues.some(j => ['queued', 'uploading', 'processing'].includes(j.status));
  const completedCount = jobValues.filter(j => j.status === 'completed').length;
  const failedCount = jobValues.filter(j => j.status === 'error').length;
  const totalCount = jobValues.length;

  return (
    <UploadContext.Provider value={{
      jobs, showPanel, setShowPanel,
      upload, clearCompleted,
      hasActive, completedCount, failedCount, totalCount,
    }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadContext() {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploadContext must be used inside UploadProvider');
  return ctx;
}
